import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  inject,
  OnInit,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { EditorStyle, LabelMode } from 'devextreme/common';
import { SnackBarService } from 'src/app/_helpers/snack-bar.service';
import { ValidacionForm } from 'src/app/_helpers/validacion-form.service';
import { FocusService } from 'src/app/_helpers/focus.service';
import {
  ProveedorInclude,
  ProveedorService,
} from 'src/app/_services/proveedor.service';
import { Proveedor } from 'src/app/_models/proveedor.model';
import { environment } from 'src/environments/environment';
import { Contacto } from 'src/app/_models/contacto.model';
import { Sede } from 'src/app/_models/_enums/sede.enum';
import { AutentificacionService } from 'src/app/_services/autentificacion.service';
import { RolUsuario } from 'src/app/_models/_enums/rolUsuario.enum';
import {
  UsuarioInclude,
  UsuarioService,
} from 'src/app/_services/usuario.service';
import jspdf from 'jspdf';
import html2canvas from 'html2canvas';
import { ErrorHandlerService } from 'src/app/_helpers/error-handler.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subject } from 'rxjs';
import {
  formatearTelefono,
  puedeDescargarAnexos,
  puedeDescargarCondiciones,
} from 'src/app/_helpers/utils';

@Component({
  selector: 'app-modal-ver-condiciones',
  templateUrl: './modal-ver-condiciones.component.html',
  styleUrls: ['./modal-ver-condiciones.component.scss'],
})
export class ModalVerCondicionesComponent implements OnInit, AfterViewInit {
  @HostBinding('class.vista-movil') get esVistaMovil() {
    return this.esResolucionMovil;
  }

  private proveedorService = inject(ProveedorService);
  private usuarioService = inject(UsuarioService);
  public validacionService = inject(ValidacionForm);
  private snackBarService = inject(SnackBarService);
  private errorHandlerService = inject(ErrorHandlerService);
  private matDialog = inject(MatDialog);
  public focusService = inject(FocusService);
  private elementRef = inject(ElementRef);
  autentificacionService = inject(AutentificacionService);
  private responsive = inject(BreakpointObserver);
  private cdr = inject(ChangeDetectorRef);

  formatearTelefono = formatearTelefono;

  idProveedor: number;
  proveedor: Proveedor;
  hayCambios: boolean = false;
  marcaAguaImg: string;
  hoy: Date = new Date();
  esResolucionMovil: boolean = !this.responsive.isMatched([
    Breakpoints.WebLandscape,
  ]);
  puedeDescargarCondiciones = puedeDescargarCondiciones;
  puedeDescargarAnexos = puedeDescargarAnexos;
  panelCargaVisible: boolean = false;
  private destroy$ = new Subject<void>();

  urlLogo = environment.imagenUrl;
  stylingMode: EditorStyle = 'outlined';
  labelMode: LabelMode = 'floating';

  constructor(
    public dialogRef: MatDialogRef<ModalVerCondicionesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { idProveedor: number },
  ) {
    this.idProveedor = this.data.idProveedor;

    if (!this.idProveedor) {
      this.snackBarService.showError('Error al abrir condiciones');
      this.dialogRef.close(false);
      return;
    }
  }

  ngOnInit() {
    this.cargarProveedor();
    this.proveedorService
      .registrarVisitaCondiciones(this.idProveedor)
      .subscribe();

    this.crearMarcaAgua();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.focusService.focusPrimerCampo(this.elementRef);
    }, 200);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cargarProveedor() {
    if (!this.idProveedor) return;

    this.proveedorService
      .getProveedor({
        id: this.idProveedor,
        includes: [
          ProveedorInclude.Contactos,
          ProveedorInclude.Condiciones,
          ProveedorInclude.Provincia,
        ],
      })
      .subscribe({
        next: (proveedor) => {
          if (proveedor.contactos) {
            proveedor.contactos = proveedor.contactos.filter(
              (contacto) => contacto.mostrarWeb === true,
            );
          }
          this.proveedor = proveedor;
        },
        error: (error) => {
          this.errorHandlerService.procesarError(
            error,
            'Error al cargar proveedor',
          );
          this.cerrarModal();
        },
      });
  }

  private crearMarcaAgua(): void {
    const usuario = this.autentificacionService.userInfo;

    this.usuarioService
      .getUsuario({
        id: usuario.id,
        includes: [UsuarioInclude.Asociado, UsuarioInclude.Proveedor],
      })
      .subscribe({
        next: (usuario) => {
          let textoMarcaAgua = 'Unifersa';
          if (usuario.proveedor && usuario.proveedor.codigo)
            textoMarcaAgua = usuario.proveedor.codigo;
          else if (usuario.asociado && usuario.asociado.codigo)
            textoMarcaAgua = usuario.asociado.codigo;

          this.setMarcaAgua(textoMarcaAgua);
        },
      });
  }

  private setMarcaAgua(texto: string): void {
    let canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;

    let ctx = canvas.getContext('2d');
    ctx.font = '16px Roboto';
    ctx.fillStyle = '#DDF2FF'; // Color azul claro
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.translate(canvas.width / 2, canvas.height / 2);

    // 35º
    ctx.rotate(-Math.PI / 6);

    // Dibujar el texto en el centro
    ctx.fillText(texto, 0, 0);

    this.marcaAguaImg = canvas.toDataURL();
  }

  cerrarModal() {
    this.dialogRef.close(this.hayCambios);
  }

  calculateProvincias(rowData: Contacto): string {
    if (!rowData.provinciasReales || rowData.provinciasReales.length === 0)
      return 'Todas';

    return rowData.provinciasReales
      .map((provincia) => provincia.nombre)
      .join(', ');
  }

  calculateSede(rowData: Contacto): string {
    if (rowData.sede === null) return '';

    return Sede[rowData.sede];
  }

  /**
   * Descarga las condiciones y los anexos (en caso existir)
   */
  descargarCondiciones() {
    const currentDate = new Date()
      .toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
      .replace(/\//g, '-');
    const documento = document.getElementById('documento');
    if (!documento) return;

    this.panelCargaVisible = true;
    this.cdr.detectChanges();
    setTimeout(() => {
      documento.style.borderWidth = '0';
      const fecha = documento.getElementsByClassName('fecha')[0] as HTMLElement;
      fecha.style.display = 'block';
      html2canvas(documento, {
        scale: 1,
        useCORS: true,
        backgroundColor: '#ffffff',
      }).then((canvas) => {
        const imgData = canvas.toDataURL('image/jpeg');
        const pdf = new jspdf('p', 'mm', 'a4');

        const pageWidth = 210; // mm
        const pageHeight = 297; // mm
        const margin = 0; // mm de margen
        const pdfUsableWidth = pageWidth - margin * 2;
        const pdfUsableHeight = pageHeight - margin * 2;

        // Escalado para que el contenido entre en A4
        // Escala por ancho
        const scale = Math.min(
          pdfUsableWidth / canvas.width,
          pdfUsableHeight / canvas.height,
        );
        const finalWidth = canvas.width * scale;
        const finalHeight = canvas.height * scale;
        const x = margin + (pdfUsableWidth - finalWidth) / 2;
        const y = margin;

        pdf.addImage(imgData, 'JPEG', x, y, finalWidth, finalHeight);
        pdf.save(`${this.proveedor.razonSocial}_${currentDate}.pdf`);
        this.descargarAnexos();
        this.panelCargaVisible = false;
      });
      documento.style.borderWidth = '1px';
      fecha.style.display = 'none';
    }, 300);
  }

  descargarAnexos() {
    if (!this.proveedor || !this.proveedor.anexoNombre) return;

    this.proveedorService.getAnexo(this.proveedor.id).subscribe({
      next: (data) => {
        if (data.archivo) {
          const base64 = data.archivo;
          const fileName = `${this.proveedor.anexoNombre}`;

          const byteCharacters = atob(base64);
          const byteNumbers = new Array(byteCharacters.length);

          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }

          const byteArray = new Uint8Array(byteNumbers);

          const blob = new Blob([byteArray]);
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.click();

          window.URL.revokeObjectURL(url);
        } else {
          this.snackBarService.showError('Anexo no disponible en este momento');
        }
      },
      error: () => {
        this.snackBarService.showError('Error al descargar el anexo');
      },
    });
  }
}
